/**
 * follow-camera.ts — pure camera state machine for the Experimental Premium Live
 * Map Layer (PLA-262 / PLA-266).
 *
 * CONTRACT
 * --------
 * This module is a pure, framework-free reducer. It imports only its equally pure
 * sibling `map-tilt` (no DOM, no google.maps, no React) so it stays unit-testable
 * in isolation and reusable from any map renderer. The map integration layer owns
 * side effects: it feeds events in via {@link computeCamera} and applies
 * `state.camera` (when non-null) to the real map.
 *
 * Modes:
 * - `overview`  — camera is NOT driven by the car; caller fits bounds / free pan.
 * - `follow`    — camera tracks the car: every `carMoved` emits a camera target
 *                 `{ center, zoom, animate }` with approach-aware zoom.
 * - `paused`    — user gestured while following; tracking is suspended until
 *                 `resumeFollow` (the integration layer auto-resumes after an
 *                 idle period).
 *
 * Rules:
 * - `follow` is only reachable while `isLiveDriving && !multiOrder`
 *   (FleetHub multi-order tracking is pinned to `overview`).
 * - `userGesture` while following -> `paused`.
 * - `resumeFollow` while paused -> `follow` (re-emits a camera target from the
 *   last known car position so the resume is immediate); falls back to
 *   `overview` when follow is no longer eligible.
 * - `isLiveDriving: false` -> `overview` (always, from any mode).
 * - `reducedMotion: true` -> emitted camera targets carry `animate: false`; the
 *   camera still recenters functionally, just without easing.
 *
 * Approach zoom ({@link approachZoom}): monotonically non-increasing as distance
 * to the destination grows. >= 5000 m clamps to zoom 13, <= 600 m clamps to
 * zoom 17, with logarithmic interpolation in between, rounded to 0.25 steps.
 */

import { approachTilt } from "./map-tilt"

/** Camera follow mode. */
export type CameraMode = "overview" | "follow" | "paused"

/** Geographic position (matches google.maps.LatLngLiteral shape, no import). */
export interface LatLng {
  lat: number
  lng: number
}

/** A concrete camera move for the integration layer to apply to the map. */
export interface CameraTarget {
  center: LatLng
  zoom: number
  /**
   * Target camera tilt (deg), ramped with zoom by {@link approachTilt} — 0 when
   * far (flat), up to MAX_TILT_DEG (32.5°) close in (ADR-0033). The integration
   * layer eases the live tilt toward this; overview/paused emit no target so the
   * hook owns flattening on mode exit.
   */
  tilt: number
  /** False when the user prefers reduced motion — jump-cut instead of easing. */
  animate: boolean
}

/** Configuration inputs that gate follow-mode eligibility and motion. */
export interface FollowCameraConfig {
  /** Respect prefers-reduced-motion: recenter without easing. */
  reducedMotion: boolean
  /** FleetHub multi-order view — pinned to overview, never follows one car. */
  multiOrder: boolean
}

/** Full machine state. Treat as immutable; `computeCamera` returns a new state. */
export interface FollowCameraState {
  mode: CameraMode
  /** Last known car position (null until the first `carMoved`). */
  carPosition: LatLng | null
  /** Last known distance to destination in meters (null until first `carMoved`). */
  distanceToDestinationMeters: number | null
  /** Whether the order is in a live-driving phase (driver en route / return). */
  isLiveDriving: boolean
  config: FollowCameraConfig
  /**
   * Camera move to apply for THIS transition, or null when the camera should not
   * move (overview / paused, or no position known yet). Consumers apply it once;
   * it is recomputed (or nulled) on every event.
   */
  camera: CameraTarget | null
}

/** Events accepted by {@link computeCamera}. */
export type FollowCameraEvent =
  | { type: "carMoved"; position: LatLng; distanceToDestinationMeters: number }
  | { type: "userGesture" }
  | { type: "resumeFollow" }
  | { type: "livePhaseChanged"; isLiveDriving: boolean }
  | { type: "configChanged"; reducedMotion: boolean; multiOrder: boolean }

// --- Approach-zoom tuning (Google Maps web zoom levels) -------------------------

const FAR_DISTANCE_METERS = 5000
// 600 m (was 150): extruded 3D buildings only render at zoom >= 17, so we reach
// full zoom 17 + tilt 32.5° over the FINAL ~600 m of every leg — the 3D city
// engages earlier instead of flashing for the last 150 m (ADR-0033 follow-up).
const NEAR_DISTANCE_METERS = 600
const FAR_ZOOM = 13
const NEAR_ZOOM = 17
const ZOOM_STEP = 0.25

/**
 * Approach-aware zoom: monotonically non-increasing as distance grows.
 *
 * - `distanceMeters >= 5000` -> 13
 * - `distanceMeters <= 600`  -> 17
 * - in between: logarithmic interpolation (distance perception is roughly
 *   logarithmic on a slippy map — each zoom level halves the viewport span),
 *   rounded to the nearest 0.25.
 * - non-finite input -> 13 (safe, zoomed-out fallback).
 */
export function approachZoom(distanceMeters: number): number {
  if (!Number.isFinite(distanceMeters)) return FAR_ZOOM
  if (distanceMeters >= FAR_DISTANCE_METERS) return FAR_ZOOM
  if (distanceMeters <= NEAR_DISTANCE_METERS) return NEAR_ZOOM

  const t =
    (Math.log(distanceMeters) - Math.log(NEAR_DISTANCE_METERS)) /
    (Math.log(FAR_DISTANCE_METERS) - Math.log(NEAR_DISTANCE_METERS))
  const raw = NEAR_ZOOM - t * (NEAR_ZOOM - FAR_ZOOM)
  const stepped = Math.round(raw / ZOOM_STEP) * ZOOM_STEP
  // Guard against float drift outside the clamp band.
  return Math.min(NEAR_ZOOM, Math.max(FAR_ZOOM, stepped))
}

// --- Web Mercator pixel offset ----------------------------------------------------

const TILE_SIZE = 256
/** sin(lat) clamp matching Google's Mercator projection cutoff (~±85.0511°). */
const MERCATOR_SINY_LIMIT = 0.9999

/**
 * Shifts a geographic point by a screen-pixel delta at a given zoom level
 * (Web Mercator: the world is `256 * 2^zoom` px wide/tall).
 *
 * SCREEN-SPACE CONVENTION: world-pixel y grows toward the SOUTH, exactly like
 * screen y grows toward the bottom. So a positive `dyPx` shifts the returned
 * CENTER toward the bottom of the screen — when the map then centers on it,
 * the originally tracked point renders HIGHER on screen by `dyPx`. Likewise a
 * positive `dxPx` shifts the center right (tracked point renders further left).
 *
 * Latitude is clamped to the Mercator valid range (~±85.0511°); longitude is
 * wrapped to [-180, 180).
 */
export function offsetLatLngByPixels(
  center: LatLng,
  dxPx: number,
  dyPx: number,
  zoom: number,
): LatLng {
  const worldPx = TILE_SIZE * Math.pow(2, zoom)

  // Project to world pixels.
  const siny = Math.min(
    MERCATOR_SINY_LIMIT,
    Math.max(-MERCATOR_SINY_LIMIT, Math.sin((center.lat * Math.PI) / 180)),
  )
  const x = worldPx * (0.5 + center.lng / 360) + dxPx
  const y = worldPx * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) + dyPx

  // Unproject. Clamp y so the latitude stays inside the Mercator valid range.
  const clampedY = Math.min(worldPx, Math.max(0, y))
  const lat =
    (180 / Math.PI) *
    (2 * Math.atan(Math.exp(Math.PI * (1 - (2 * clampedY) / worldPx))) - Math.PI / 2)
  const rawLng = (x / worldPx - 0.5) * 360
  const lng = ((((rawLng + 180) % 360) + 360) % 360) - 180

  return { lat, lng }
}

// --- State machine ---------------------------------------------------------------

/** Initial state: overview, nothing known, no camera move. */
export function createInitialState(config?: Partial<FollowCameraConfig>): FollowCameraState {
  return {
    mode: "overview",
    carPosition: null,
    distanceToDestinationMeters: null,
    isLiveDriving: false,
    config: {
      reducedMotion: config?.reducedMotion ?? false,
      multiOrder: config?.multiOrder ?? false,
    },
    camera: null,
  }
}

/** Follow mode is only reachable while live driving a single order. */
function isFollowEligible(state: FollowCameraState): boolean {
  return state.isLiveDriving && !state.config.multiOrder
}

/** Build the camera target for the current car position, or null if unknown. */
function followCamera(state: FollowCameraState): CameraTarget | null {
  if (!state.carPosition || state.distanceToDestinationMeters === null) return null
  // Derive tilt from the SAME zoom we emit, so the lean and the zoom can never
  // disagree within a render (ADR-0033): flat far out, leaning in on approach.
  const zoom = approachZoom(state.distanceToDestinationMeters)
  return {
    center: state.carPosition,
    zoom,
    tilt: approachTilt(zoom),
    animate: !state.config.reducedMotion,
  }
}

/**
 * Pure reducer: `(prevState, event) -> nextState`. Never mutates `prevState`.
 * Apply `nextState.camera` to the map iff it is non-null.
 */
export function computeCamera(
  prevState: FollowCameraState,
  event: FollowCameraEvent,
): FollowCameraState {
  switch (event.type) {
    case "carMoved": {
      const next: FollowCameraState = {
        ...prevState,
        carPosition: event.position,
        distanceToDestinationMeters: event.distanceToDestinationMeters,
        camera: null,
      }
      if (next.mode === "follow" && isFollowEligible(next)) {
        next.camera = followCamera(next)
      }
      return next
    }

    case "userGesture": {
      if (prevState.mode === "follow") {
        return { ...prevState, mode: "paused", camera: null }
      }
      // Overview pans freely; paused stays paused.
      return { ...prevState, camera: null }
    }

    case "resumeFollow": {
      if (prevState.mode === "paused" && isFollowEligible(prevState)) {
        const next: FollowCameraState = {
          ...prevState,
          mode: "follow",
          camera: null,
        }
        next.camera = followCamera(next)
        return next
      }
      // Not eligible (live phase ended / multi-order) -> settle on overview.
      if (prevState.mode === "paused") {
        return { ...prevState, mode: "overview", camera: null }
      }
      return { ...prevState, camera: null }
    }

    case "livePhaseChanged": {
      const next: FollowCameraState = {
        ...prevState,
        isLiveDriving: event.isLiveDriving,
        camera: null,
      }
      if (!event.isLiveDriving) {
        // Leaving live driving always lands on overview.
        return { ...next, mode: "overview" }
      }
      // Entering live driving auto-engages follow from overview (if eligible);
      // a paused user stays paused — never yank the camera back uninvited.
      if (next.mode === "overview" && isFollowEligible(next)) {
        next.mode = "follow"
        next.camera = followCamera(next)
      }
      return next
    }

    case "configChanged": {
      const next: FollowCameraState = {
        ...prevState,
        config: { reducedMotion: event.reducedMotion, multiOrder: event.multiOrder },
        camera: null,
      }
      if (next.config.multiOrder) {
        // Multi-order is pinned to overview, from any mode.
        return { ...next, mode: "overview" }
      }
      if (!isFollowEligible(next) && next.mode !== "overview") {
        return { ...next, mode: "overview" }
      }
      // Becoming eligible again (e.g. multi-order toggled off mid-drive)
      // re-engages follow from overview only.
      if (next.mode === "overview" && isFollowEligible(next)) {
        next.mode = "follow"
        next.camera = followCamera(next)
      }
      return next
    }
  }
}
